import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
    MHeader,
    MButton,
    LoadingContext,
    M_StateActions,
    NavigateContext,
} from 'src/M_Components';

import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from 'src/components/Forms/DynamicForm';
import '../pages.css';

/* ================================================================================================
 * toCamel
 * Small helper to build DTO keys from Options metadata (Header/Name) using lowerCamelCase.
 * ============================================================================================== */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/* ================================================================================================
 * isBoolean
 * Type guard for options to coerce booleans in the payload.
 * ============================================================================================== */
const isBoolean = (opt) => opt?.Type === 'boolean' || opt?.Type === 'bool';

export default function RouteConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const M_State = useSelector((state) => state.StateKeyValue);
    const selectedTab = useSelector(s => s.StateKeyValue.find(o => o.key === 'selectedTab')?.value);

    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    // One-shot globals
    const globalOperation = M_State.find(obj => obj.key === 'operation')?.value;
    const globalLine = M_State.find(obj => obj.key === 'RouteLine')?.value;

    // Tab-scoped state
    const [id, setId] = useState(M_State.find(obj => obj.key === 'selectedTab')?.value);
    const [operation, setOperation] = useState(globalOperation ?? 'Error');
    const [sessionLine, setSessionLine] = useState(globalLine ?? {});
    const [routeInfo, setRouteInfo] = useState({});
    const [isFirst, setIsFirst] = useState(true);

    const isViewMode = (operation || '').toLowerCase() === 'view';

    // DynamicForm bits
    const [schema, setSchema] = useState([]);  // Routes/Options
    const formRef = useRef(null);              // imperative Dynamic Form API

    // Services
    const svcRoutes = useMemo(() => new BufferService('Routes', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcCreate = useMemo(() => new BufferService('Routes/create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcUpdate = useMemo(() => new BufferService('Routes/update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

    /* ------------------------------------------------------------------------------------------------
     * Tab label
     * ------------------------------------------------------------------------------------------------ */
    useEffect(() => {
        if (!selectedTab || !operation) return;
        const key =
            operation === 'Create' ? 'LabelNewRoute'
                : operation === 'Edit' ? 'LabelEditRoute'
                    : 'LabelViewRoute';
        const name = sessionLine?.Description ?? routeInfo?.Description ?? '';
        const base = t(key);
        const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
        renameTab(String(selectedTab), label);
    }, [selectedTab, operation, sessionLine?.Description, routeInfo?.Description]);

    /* ------------------------------------------------------------------------------------------------
     * Mount: restore snapshot, consume globals, bootstrap
     * ------------------------------------------------------------------------------------------------ */
    useEffect(() => {
        const sessionForThisTab = M_State.find(o => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find(x => x.key === k)?.value;

        const snapInfo = pick('routeInfo');
        const snapIF = pick('isFirst');
        const snapOP = pick('operation');
        const snapSL = pick('sessionLine');
        const snapSchema = pick('df.schema');

        if (snapInfo) setRouteInfo(snapInfo);
        if (snapSchema) setSchema(snapSchema);
        if (snapIF !== undefined) setIsFirst(snapIF); else if (isFirst) setIsFirst(false);

        const seedOperation = (globalOperation !== undefined ? globalOperation : snapOP);
        const seedLine = (globalLine !== undefined ? globalLine : snapSL);

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) setSessionLine(seedLine);

        if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
        if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'RouteLine' }));

        bootstrap(seedOperation ?? operation, seedLine ?? sessionLine);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ------------------------------------------------------------------------------------------------
     * Persist snapshot (keeps unsaved edits when switching tabs)
     * - We also merge current RHF values to preserve in-flight edits even if DF hasn't emitted change yet.
     * ------------------------------------------------------------------------------------------------ */
    useEffect(() => {
        if (!id) return;

        // Merge current DF values when not in view mode
        const currentFormValues = formRef.current?.getValues?.() || {};
        const infoToPersist =
            (operation || '').toLowerCase() === 'view'
                ? routeInfo
                : { ...routeInfo, ...currentFormValues };

        const sessionData = [
            { key: 'routeInfo', value: infoToPersist },
            { key: 'isFirst', value: isFirst },
            { key: 'operation', value: operation },
            { key: 'sessionLine', value: sessionLine },
            { key: 'df.schema', value: schema },
        ];
        dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, [id, routeInfo, isFirst, operation, sessionLine, schema, dispatch]);

    /* ------------------------------------------------------------------------------------------------
     * React to selectedTab changes
     * ------------------------------------------------------------------------------------------------ */
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
    }, [M_State, id]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ------------------------------------------------------------------------------------------------
     * Keep Options translated
     * ------------------------------------------------------------------------------------------------ */
    useEffect(() => {
        if (!operation || isFirst) return;
        fetchOptions(operation, sessionLine, { skipDetails: true });

        const key =
            operation === 'Create' ? 'LabelNewRoute'
                : operation === 'Edit' ? 'LabelEditRoute'
                    : 'LabelViewRoute';
        const name = sessionLine?.Description ?? routeInfo?.Description ?? '';
        const base = t(key);
        const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
        renameTab(String(selectedTab), label);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // Restore another tab snapshot or bootstrap
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);
        const M_StateData = M_State.find(obj => obj.key === sessionID)?.value;
        if (M_StateData) {
            const map = {};
            M_StateData.forEach(o => { map[o.key] = o.value; });
            setRouteInfo(map['routeInfo'] || {});
            setIsFirst(map['isFirst'] ?? false);
            setOperation(map['operation']);
            setSessionLine(map['sessionLine']);
            if (map['df.schema']) setSchema(map['df.schema']);
            ctx.setIsLoad(false);
            return;
        }
        bootstrap(operation, sessionLine);
    };

    /* ------------------------------------------------------------------------------------------------
     * bootstrap: Options then details (if not Create)
     * ------------------------------------------------------------------------------------------------ */
    const bootstrap = async (op, line) => {
        ctx.setIsLoad(true);
        try {
            await fetchOptions();
            if ((op || '').toLowerCase() !== 'create' && (line?.Id || sessionLine?.Id)) {
                await fetchDetails(line?.Id || sessionLine?.Id);
            }
        } finally {
            ctx.setIsLoad(false);
        }
    };

    /* ------------------------------------------------------------------------------------------------
     * fetchOptions: Routes/Options -> DynamicForm schema
     * ------------------------------------------------------------------------------------------------ */
    const fetchOptions = async () => {
        try {
            const res = await svcRoutes.options();
            if (isFailurePayload(res)) {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                return;
            }
            setSchema(Array.isArray(res) ? res : []);
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
        }
    };

    /* ------------------------------------------------------------------------------------------------
     * fetchDetails: GET route by Id; Dynamic Form will hydrate/mirror
     * ------------------------------------------------------------------------------------------------ */
    const fetchDetails = async (idToGet) => {
        try {
            const res = await svcRoutes.getId(idToGet);
            if (isFailurePayload(res)) {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                return;
            }
            setRouteInfo(res || {});
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
        }
    };

    /* ------------------------------------------------------------------------------------------------
     * getRefresh: re-fetch Options and details (if applicable)
     * ------------------------------------------------------------------------------------------------ */
    const getRefresh = (line, op = operation) => {
        const effectiveLine = line || sessionLine;
        if ((op || '').toLowerCase() !== 'create' && !effectiveLine?.Id) {
            ctx.setIsLoad(false);
            return;
        }
        ctx.setIsLoad(true);
        Promise.all([
            fetchOptions(),
            ((op || '').toLowerCase() !== 'create' ? fetchDetails(effectiveLine?.Id) : Promise.resolve())
        ]).finally(() => ctx.setIsLoad(false));
    };

    /* ------------------------------------------------------------------------------------------------
     * Dynamic Form change stream → keep unsaved edits in tab snapshot
     * ------------------------------------------------------------------------------------------------ */
    const handleFormValuesChange = useCallback((patch) => {
        if (!patch) return;
        setRouteInfo(prev => ({ ...prev, ...patch }));
    }, []);

    /* ------------------------------------------------------------------------------------------------
     * Payload builder from Dynamic Form values + Options
     * - Uses Header (when present) or Name to read the Dynamic Form value.
     * - LowerCamelCase keys for DTO.
     * - Booleans coerced.
     * - Skips "Inactive" on Create (legacy rule).
     * ------------------------------------------------------------------------------------------------ */
    const buildPayloadFromValues = (values) => {
        const opts = Array.isArray(schema) ? schema : [];
        const entries = [];

        for (const opt of opts) {
            if (!opt) continue;

            const formKey = opt.Header || opt.Name;
            const dtoKey = toCamel(opt.Header || opt.Name);
            let v = values?.[formKey];

            if (isBoolean(opt)) v = !!v;

            if ((operation || '').toLowerCase() === 'create' && (opt.Name === 'Inactive' || opt.Header === 'Inactive')) {
                continue;
            }
            if (v !== undefined) entries.push([dtoKey, v]);
        }

        return Object.fromEntries(entries);
    };

    /* ------------------------------------------------------------------------------------------------
     * onSubmitDynamic: create/update with minimal validation (Description required)
     * ------------------------------------------------------------------------------------------------ */
    const onSubmitDynamic = useCallback(async (values) => {
        ctx.setIsLoad(true);
        try {
            const payload = buildPayloadFromValues(values);

            if (!payload.description) {
                toastMessages('error', '', t('MandatoryFields'));
                return;
            }

            const isCreate = (operation || '').toLowerCase() === 'create';
            const res = isCreate
                ? await svcCreate.post(payload)
                : await svcUpdate.update(sessionLine?.Id, payload);

            if (isFailurePayload(res)) {
                toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
                return;
            }

            toastMessages('success', '', isCreate ? t('RouteCreated') : t('RouteUpdated'));
            handleTabClose(id);
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
        } finally {
            ctx.setIsLoad(false);
        }
    }, [operation, svcCreate, svcUpdate, sessionLine?.Id, id, t, toastMessages, ctx, schema]);

    /* ------------------------------------------------------------------------------------------------
     * Save trigger via Dynamic Form imperative API
     * ------------------------------------------------------------------------------------------------ */
    const triggerFormSubmit = () => formRef.current?.submit?.();

    return (
        <>
            <MHeader element={
                <>
                    <MButton
                        tooltip={t('RefreshTable')}
                        icon={<i className="pi pi-refresh iconTable"></i>}
                        onClick={() => getRefresh(sessionLine, operation)}
                        label={t('RefreshTable')}
                    />
                    {!isViewMode && (
                        <MButton
                            tooltip={t('Save')}
                            icon={<i className="pi pi-save iconTable"></i>}
                            onClick={triggerFormSubmit}
                            label={t('Save')}
                        />
                    )}
                </>
            } />

            <div
                style={{
                    padding: '20px',
                    background: 'var(--color-white)'
                }}>
                <div className='firstDivContainer'>
                    <DynamicForm
                        key={`df-routes-${selectedTab}-${operation}-${sessionLine?.Id ?? 'new'}`}
                        formRef={formRef}
                        mode={(operation || 'View').toLowerCase()}
                        schema={schema}
                        data={routeInfo}
                        onValuesChange={handleFormValuesChange}
                        onSubmit={onSubmitDynamic}
                        title={t('Route Information')}
                        containerClassName="alignInputsUserForm"
                    />
                </div>
            </div>
        </>
    );
}

RouteConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func
};
